import { Fragment } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
	AlertMessage,
	CardAuthorMark,
	FitToViewport,
	YoutubeWidget,
} from '@quizzach/components';
import { Colors, Text, TextSize } from '@quizzach/styles';
import { NavButtonWrapper, QuizIntroContainer } from '@quizzach/play-styles';
import { useEffect, useState } from 'react';
import { ApiFailedReason, QuizOutletContextType } from '@quizzach/interfaces';
import { scrollToTop } from '@quizzach/utils';
import { StartQuizFilled } from '@quizzach/assets';
import { useAuth, useWindowListener } from '@quizzach/contexts';
import { Reveal } from '@quizzach/motion';
import { useTranslation } from 'react-i18next';

const MODE_PATTERNS: Record<string, Array<'q' | 'a'>> = {
	qaqa: ['q', 'a', 'q', 'a'],
	qqaa: ['q', 'q', 'a', 'a'],
};

export const QuizIntro = () => {
	const { t } = useTranslation();
	const { quiz, quizType, error, halftime, halfLength, mode } =
		useOutletContext<QuizOutletContextType>();

	const { uuid } = useParams<{ uuid: string }>();
	const { profileInfo } = useAuth();

	const [isStartHovered, setIsStartHovered] = useState(false);
	const { touchDevice } = useWindowListener();

	const navigate = useNavigate();

	const handleStartQuiz = () => {
		if (navigator.vibrate) navigator.vibrate(30);
		const hasChapters =
			quiz?.premade_quiz?.chapters && quiz.premade_quiz.chapters.length > 0;
		const firstChapterStartsAtOne =
			hasChapters && quiz?.premade_quiz?.chapters?.[0]?.chapter_start === 1;

		if (quiz?.premade_quiz?.puzzle) {
			navigate(`/quiz/${uuid}/puzzle-intro`);
		} else if (firstChapterStartsAtOne) {
			navigate(`/quiz/${uuid}/question-chapter/1`);
		} else {
			navigate(`/quiz/${uuid}/question/1`);
		}
	};

	const checkQuizType = (quizType: string) => {
		switch (quizType) {
			case 'generator':
				return t('quiz.intro.typeGenerator');
			case 'quick':
				return t('quiz.intro.typeQuick');
			case 'premade':
				return t('quiz.intro.specialEdition');
			default:
				return '';
		}
	};

	useEffect(() => {
		scrollToTop();
	}, []);

	const pattern = MODE_PATTERNS[mode] ?? MODE_PATTERNS.qqaa;

	const introBody = (
		<>
			<Reveal className='quiz-start-head'>
				<div className='quiz-start-headmain'>
					<span className='quiz-start-greet'>
						{t('quiz.intro.timeForQuiz', {
							nickname: profileInfo?.nickname,
						})}
					</span>
					<h2 className='quiz-start-title'>
						{quiz?.premade_quiz?.title ?? checkQuizType(quizType)}
					</h2>
				</div>
				{quiz?.premade_quiz && (
					<span className='quiz-start-author'>
						<CardAuthorMark
							displayName={quiz.premade_quiz.author.display_name}
						/>
					</span>
				)}
			</Reveal>
			<div className='quiz-start-rows'>
				<Reveal className='quiz-start-row'>
					<span className='quiz-start-lab'>
						{t('quiz.intro.questionCount')}
					</span>
					<span className='quiz-start-val'>{quiz?.questions.length - 1}</span>
				</Reveal>
				<Reveal className='quiz-start-row grow'>
					<span className='quiz-start-lab'>{t('quiz.intro.playMode')}</span>
					<span className='quiz-start-val'>
						{pattern.map((step, index) => (
							<Fragment key={index}>
								{index > 0 && <span className='sep'>-</span>}
								<span className={step === 'a' ? 'ans' : undefined}>
									{t(step === 'a' ? 'quiz.mode.answer' : 'quiz.mode.question')}
								</span>
							</Fragment>
						))}
					</span>
				</Reveal>
				{halftime && mode === 'qqaa' && (
					<Reveal className='quiz-start-row'>
						<span className='quiz-start-lab'>{t('quiz.intro.halftime')}</span>
						<span className='quiz-start-val'>
							{t('quiz.intro.halftimeAfter', { n: halfLength })}
						</span>
					</Reveal>
				)}
				{quiz?.premade_quiz?.playlist_url && (
					<Reveal className='quiz-start-row grow'>
						<span className='quiz-start-lab'>
							{t('quiz.intro.musicBackground')}
						</span>
						<span className='quiz-start-val'>
							<YoutubeWidget
								quizTitle={quiz?.premade_quiz?.title || ''}
								quizPlaylistUrl={quiz?.premade_quiz?.playlist_url || ''}
							/>
						</span>
					</Reveal>
				)}
			</div>
		</>
	);

	return (
		<QuizIntroContainer data-id='quiz-intro-container-01'>
			{error && (
				<AlertMessage
					code={ApiFailedReason.default}
					type='error'
					style={{ margin: '0px auto' }}
				/>
			)}
			{!error && (
				<>
					<span className='quiz-start-space' aria-hidden='true' />
					{/* Mobile scrolls naturally; the fit-to-viewport scaling is a
					    desktop/presentation concern. */}
					{touchDevice ? introBody : <FitToViewport>{introBody}</FitToViewport>}
					<span className='quiz-start-space bottom' aria-hidden='true' />
				</>
			)}
			<NavButtonWrapper
				onClick={handleStartQuiz}
				onMouseEnter={() => setIsStartHovered(true)}
				onMouseLeave={() => setIsStartHovered(false)}
			>
				<StartQuizFilled
					size={40}
					color={isStartHovered ? Colors.textReverse : Colors.brandMain}
				/>
				<Text
					$size={TextSize.l}
					color={isStartHovered ? Colors.textReverse : Colors.textBrand}
					style={{ margin: 0 }}
				>
					{t('quiz.intro.start')}
				</Text>
			</NavButtonWrapper>
		</QuizIntroContainer>
	);
};
